import { useSyncExternalStore } from "react";

/**
 * The current software-keyboard height, in CSS pixels, as a tiny observable
 * store.
 *
 * WHY THIS EXISTS AT ALL
 * ---------------------
 * The app targets Android API 36. From Android 15 the system draws every app
 * edge-to-edge and `adjustResize` no longer shrinks the window when the IME
 * opens — the keyboard is simply an inset the app is expected to consume. The
 * WebView therefore keeps its full height, `100dvh` keeps reporting the whole
 * screen, and anything anchored to the bottom of the viewport (the chat
 * composer) ends up *behind* the keyboard rather than above it.
 *
 * @capacitor/keyboard's Android implementation does not resize anything for us
 * either: `possiblyResizeChildOfContent` only runs under `resizeOnFullScreen`,
 * and its `resize` option is iOS-only. What it does give us is the IME height,
 * already divided by display density — i.e. already in CSS pixels — so it can
 * be used as a length verbatim with no conversion.
 *
 * WHY A STORE AND NOT A CONTEXT
 * -----------------------------
 * The value changes on a native event, not on a render, and only a couple of
 * components care. A module store means the bridge that receives the event
 * knows nothing about React, the components that need the number subscribe to
 * it, and everything else is untouched — no provider added to the App tree and
 * no re-render of the whole app every time someone taps a text field.
 *
 * Nothing in this file imports @capacitor/*: it is plain state plus a CSS
 * custom property, so it is fully exercisable in jsdom. The native listener
 * lives in ./keyboardBridge.
 */

/**
 * Mirrored onto the document element so plain CSS can use it too:
 * `calc(12px + var(--kb-inset, 0px))`. The `0px` fallback is what makes every
 * consumer correct on web, where nothing ever sets this.
 */
export const KEYBOARD_INSET_VAR = "--kb-inset";

let insetPx = 0;
const subscribers = new Set();

const writeCssVar = (px) => {
  try {
    document.documentElement.style.setProperty(KEYBOARD_INSET_VAR, `${px}px`);
  } catch {
    // No document (non-DOM host). The numeric store above is still correct,
    // and a failure to paint an inset must never take the app down.
  }
};

/** Current keyboard height in CSS pixels; 0 whenever the keyboard is closed. */
export const getKeyboardInset = () => insetPx;

/**
 * Whether the soft keyboard is up, as the native plugin last reported it —
 * separate from the inset, which reads 0 when the WebView has already shrunk
 * for the keyboard (see keyboardBridge.js). Back uses it: Android hides the
 * keyboard with the first Back itself, so a press that reaches the app with a
 * field still focused but no keyboard showing must not be spent on a blur that
 * changes nothing visible (tablet QA, versionCode 11 — leaving a conversation
 * after typing took three presses).
 */
let keyboardShown = false;
export const isKeyboardShown = () => keyboardShown;
export const setKeyboardShown = (shown) => {
  keyboardShown = Boolean(shown);
};

/**
 * Record a new keyboard height.
 *
 * Anything that is not a positive finite number collapses to 0 — a keyboard
 * plugin that reports `undefined` must close the inset, not produce
 * `padding-bottom: NaNpx` and silently drop the declaration.
 */
export const setKeyboardInset = (px) => {
  const next = Number.isFinite(px) && px > 0 ? Math.round(px) : 0;
  if (next === insetPx) return;
  insetPx = next;
  writeCssVar(next);
  subscribers.forEach((notify) => notify());
};

export const subscribeKeyboardInset = (notify) => {
  subscribers.add(notify);
  return () => {
    subscribers.delete(notify);
  };
};

/**
 * Subscribe a component to the keyboard height.
 *
 * Returns 0 on web and in tests until something calls `setKeyboardInset`, so a
 * consumer can use it unconditionally.
 */
export const useKeyboardInset = () =>
  useSyncExternalStore(
    subscribeKeyboardInset,
    getKeyboardInset,
    getKeyboardInset
  );
